import React from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import "dayjs/locale/es";
import RIcon from "../../reservas/components/ReservasIcons";
import MarketingOptIn from "../../reservas/components/MarketingOptIn";
import InlineDayPicker from "../../reservas/components/InlineDayPicker";
import { resumenDeLineas } from "../../../helpers/beautyLineas";
import WaitlistJoin from "../../listaDeEspera/WaitlistJoin";
import { isWaitlistEnabled } from "../../listaDeEspera/waitlistModel";
import PhoneCountryInput from "../../reservas/components/PhoneCountryInput";
import { QtySelect, resolveUrl } from "../../reservas/components/ReservaShared";
import {
  SLOT_PERIODS,
  formatDuration,
  formatMoney,
  formatPillDate,
  payAtVenue,
  slotPeriod,
} from "../beautyBooking";

/**
 * Presentational pieces of the booking steps shared by the mobile wizard and
 * the desktop flow. They only read the booking flow (`useBeautyBookingFlow`)
 * and own no business logic. `desktop` switches between the Canchas desktop
 * markup (zone-btn, slots-grid, rdb-row, rs-row…) and the Canchas mobile
 * markup (rmob-zone-row, rmob-slot-row, rmob-rs-row…).
 */


const ProfessionalAvatar = ({ professional }) => (
  <span className="beauty-avatar">
    {professional.photo
      ? <img src={resolveUrl(professional.photo)} alt="" />
      : (professional.name || "?").trim().charAt(0).toUpperCase()}
  </span>
);

const initialsOf = (name) =>
  (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("") || "?";

/**
 * Personal-link mode: replaces the professional choice. Shows who the booking
 * is with and a way back to the normal flow ("Cambiar profesional").
 */
export const LinkedProfessionalCard = ({ flow }) => {
  const { linked, maxDurationNotice } = flow;
  if (!linked?.professional) return null;
  const { professional } = linked;
  return (
    <div className="beauty-linked-pro">
      <div className="beauty-linked-pro-row">
        <span className="beauty-avatar" aria-hidden="true">{initialsOf(professional.name)}</span>
        <div className="beauty-linked-pro-text">
          <span className="beauty-linked-pro-eyebrow">Reservás con</span>
          <strong>{professional.name}</strong>
        </div>
        <button type="button" className="beauty-link-btn" onClick={linked.leave}>Cambiar profesional</button>
      </div>
      <p className="beauty-linked-pro-note">Los servicios y precios de {professional.name}. No hay paso de profesional: el turno ya va con esta persona.</p>
      {maxDurationNotice && <Alert variant="warning" className="mb-0 mt-2">{maxDurationNotice}</Alert>}
    </div>
  );
};

/** Professional cards: "Sin preferencia" first, ineligible ones disabled. */
export const ProfessionalOptions = ({ flow, desktop = false }) => {
  const { professionalId } = flow;
  const cls = desktop
    ? { list: "zones-grid beauty-pro-grid", item: "zone-btn", selected: "selected", off: "unavailable", name: "zone-name", tag: "zone-tag" }
    : { list: "rmob-zones-list", item: "rmob-zone-row", selected: "selected", off: "unavailable", name: "rmob-zone-name", tag: "rmob-zone-tag" };

  const renderOption = ({ key, selected, eligible = true, onClick, avatar, name, sub, tag }) => (
    <button
      key={key}
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={!eligible}
      disabled={!eligible}
      className={`${cls.item}${selected ? ` ${cls.selected}` : ""}${eligible ? "" : ` ${cls.off}`}`}
      onClick={onClick}
    >
      <span className="beauty-zone-main">
        {avatar}
        <span className="beauty-zone-text">
          <span className={cls.name}>{name}</span>
          {sub && <span className="beauty-zone-sub">{sub}</span>}
        </span>
      </span>
      {tag && <span className={cls.tag}>{tag}</span>}
      {!tag && !desktop && selected && <RIcon name="check" width={18} height={18} />}
    </button>
  );

  return (
    <div className={cls.list} role="radiogroup" aria-label="Profesional">
      {renderOption({
        key: "any",
        selected: !professionalId,
        onClick: () => flow.selectProfessional(null),
        avatar: <span className="beauty-avatar"><RIcon name="users" width={18} height={18} /></span>,
        name: "Sin preferencia",
        sub: "Te asignamos el primer profesional disponible.",
      })}
      {flow.professionalOptions.map(({ professional, missing, exceeds, maxMinutes, eligible }) =>
        renderOption({
          key: professional.id,
          selected: String(professionalId) === String(professional.id),
          eligible,
          onClick: () => flow.selectProfessional(professional.id),
          avatar: <ProfessionalAvatar professional={professional} />,
          name: professional.name,
          sub: eligible
            ? professional.specialty || ""
            : exceeds
              ? `Supera su duración máxima (${formatDuration(maxMinutes)})`
              : `No realiza ${missing.map((s) => s.name).join(", ")}`,
          tag: eligible ? "" : "No disponible",
        })
      )}
    </div>
  );
};

export const ProfessionalNote = () => (
  <p className="beauty-info-note">
    <RIcon name="info" width={15} height={15} />
    Solo podés elegir profesionales que hacen todos los servicios de tu turno. El precio puede variar según el profesional.
  </p>
);

const bookableWindow = (salon) => {
  const today = dayjs().startOf("day");
  const windowDays = Number(salon.reservationWindowDays);
  return {
    minDate: today,
    maxDate: today.add(Number.isFinite(windowDays) && windowDays >= 0 ? windowDays : 365, "day"),
  };
};

/**
 * Day picker: the Canchas "Hoy / Mañana" chips plus the always-open inline
 * antd calendar (Spanish locale). Desktop and mobile share the markup; desktop
 * only adds a modifier class for the compact side-by-side card. Closed
 * weekdays and the booking window come from `flow.dateBookable`.
 */
export const DayPicker = ({ flow, salon, desktop = false }) => {
  const { minDate, maxDate } = bookableWindow(salon);
  return (
    <InlineDayPicker
      value={flow.date}
      onChange={flow.setDate}
      isBookable={flow.dateBookable}
      minDate={minDate}
      maxDate={maxDate}
      desktop={desktop}
    />
  );
};

/**
 * Slot step body: loading, error with retry, empty with next steps, or the
 * Mañana / Tarde / Noche groups. The empty-state actions are optional.
 */
export const SlotPicker = ({ flow, desktop = false, onChangeDate, onChangeProfessional }) => {
  const { availability, selectedSlot, professionalId } = flow;
  const slots = Array.isArray(availability.data?.slots) ? availability.data.slots : [];
  const groups = SLOT_PERIODS.map((period) => ({
    period,
    slots: slots.filter((slot) => slotPeriod(slot.slotStart) === period),
  })).filter((g) => g.slots.length > 0);

  // "Avisarme si se libera": el pedido sale de la selección actual (servicios con opciones y profesional).
  const waitlist = (
    <WaitlistJoin
      enabled={isWaitlistEnabled(flow.salon)}
      barId={flow.salon?._id}
      kind="beauty"
      date={flow.date}
      criteria={flow.selectionComplete && flow.items?.length ? { items: flow.items, ...(professionalId ? { professionalId } : {}) } : null}
      summary={(flow.cartServices || []).map((s) => s.name).filter(Boolean).join(" + ")}
    />
  );

  if (availability.loading) {
    return <div className="beauty-center"><Spinner animation="border" style={{ color: "var(--zoco-lime-strong)" }} /></div>;
  }
  if (availability.error) {
    return (
      <div>
        <Alert variant="danger">{availability.error}</Alert>
        <button type="button" className="r-btn-primary" onClick={flow.reloadAvailability}>Reintentar</button>
      </div>
    );
  }
  if (slots.length === 0) {
    const actions = (
      <div className="beauty-error-actions">
        {onChangeDate && <button type="button" className="r-btn-primary" onClick={onChangeDate}>Elegir otra fecha</button>}
        {professionalId && onChangeProfessional && (
          <button type="button" className="r-btn-soft" onClick={onChangeProfessional}>Cambiar profesional</button>
        )}
      </div>
    );
    const text =
      flow.maxDurationNotice ||
      `No hay horarios disponibles para esta fecha. Probá con otra fecha${professionalId ? " o con otro profesional" : ""}.`;
    return desktop ? (
      <Alert variant="light">
        <p>{text}</p>
        {actions}
        {waitlist}
      </Alert>
    ) : (
      <div className="rmob-empty">
        <p>{text}</p>
        {actions}
        {waitlist}
      </div>
    );
  }
  return (
    <>
      {groups.map(({ period, slots: periodSlots }) => (
    <div key={period} className="beauty-slot-group">
      <h4 className="beauty-group-title">{period}</h4>
      <div className={desktop ? "slots-grid" : "rmob-slots-list"}>
        {periodSlots.map((slot) => {
          const end = flow.slotEndFor(slot);
          const active = selectedSlot?.slotStart === slot.slotStart;
          return desktop ? (
            <button
              key={slot.slotStart}
              type="button"
              className={`slot-btn${active ? " selected" : ""}`}
              aria-pressed={active}
              onClick={() => flow.selectSlot(slot)}
            >
              <span className="slot-time">{slot.slotStart} hs</span>
              {end && <span className="slot-end">hasta {end}</span>}
            </button>
          ) : (
            <button
              key={slot.slotStart}
              type="button"
              className={`rmob-slot-row${active ? " selected" : ""}`}
              aria-pressed={active}
              onClick={() => flow.selectSlot(slot)}
            >
              <span className="rmob-slot-time">{slot.slotStart} hs</span>
              {end && <span className="rmob-slot-end">hasta {end}</span>}
              <RIcon name="chevR" width={18} height={18} className="rmob-slot-arrow" />
            </button>
          );
        })}
      </div>
    </div>
      ))}
      {waitlist}
    </>
  );
};

/** One "label / value" line: `rdb-row` on desktop, `rmob-rs-row` on mobile. */
const DetailRow = ({ desktop, icon, label, children }) =>
  desktop ? (
    <div className="rdb-row">
      <span><RIcon name={icon} width={14} height={14} /> {label}</span>
      <strong className="beauty-rs-value">{children}</strong>
    </div>
  ) : (
    <div className="rmob-rs-row">
      <RIcon name={icon} width={15} height={15} />
      <span>{label}</span>
      <strong className="beauty-rs-value">{children}</strong>
    </div>
  );

/** Fecha / Horario / Duración / Profesional rows of a live hold. */
export const HoldDetailRows = ({ hold, holdTotals, date, professionalLabel, professionalId, desktop = false }) => (
  <>
    <DetailRow desktop={desktop} icon="cal" label="Fecha">{formatPillDate(date)}</DetailRow>
    <DetailRow desktop={desktop} icon="clock" label="Horario">
      {hold.slotEnd ? `${hold.slotStart} a ${hold.slotEnd} hs` : `${hold.slotStart} hs`}
    </DetailRow>
    <DetailRow desktop={desktop} icon="clock" label="Duración">{formatDuration(holdTotals.durationMinutes)}</DetailRow>
    <DetailRow desktop={desktop} icon="user" label="Profesional">
      {hold.professional?.name || professionalLabel}{professionalId ? "" : " (asignado)"}
    </DetailRow>
  </>
);

/**
 * Canchas desktop totals block (aside and sum-card): price row, secure note and
 * "Ahora pagás" with its sub-lines. There is no "Tasa de servicio" row: the
 * Canchas one is a decorative fake amount, so beauty has no real value to show.
 */
export const AsideAmounts = ({ total, deposit, from = false }) => {
  const hasDeposit = Number(deposit) > 0;
  return (
    <>
      <div className="rs-div" />
      <div className="rs-row">
        <span className="k">{from ? "Desde" : "Total del turno"}</span>
        <span className="v">{formatMoney(total)}</span>
      </div>
      <div className="rs-note beauty-rs-note">
        <RIcon name="shield" width={15} height={15} />
        Reserva segura, sin costos extra de servicio.
      </div>
      <div className="rs-div" />
      <div className="rs-total">
        <span className="k">Ahora pagás</span>
        <span className="v beauty-lime">{formatMoney(hasDeposit ? deposit : 0)}</span>
      </div>
      <div className="beauty-rs-sub">
        {hasDeposit ? "Seña para reservar el turno" : "Pagás el total en el local"}
      </div>
      {hasDeposit && (
        <div className="beauty-rs-sub">Pagás en el local {formatMoney(payAtVenue(total, deposit))}</div>
      )}
    </>
  );
};

/** Total, "Ahora pagás (seña)", "Pagás en el local" and the secure line. */
export const HoldAmounts = ({ holdTotals, hasDeposit, desktop = false }) =>
  desktop ? (
    <AsideAmounts total={holdTotals.total} deposit={holdTotals.deposit} />
  ) : (
    <>
      <div className="rmob-rs-row">
        <span>Total del turno</span>
        <strong className="beauty-rs-value">{formatMoney(holdTotals.total)}</strong>
      </div>
      {hasDeposit && (
        <div className="beauty-pay-now">
          <div>
            <strong>Ahora pagás (seña)</strong>
            <small>Se descuenta del total</small>
          </div>
          <span>{formatMoney(holdTotals.deposit)}</span>
        </div>
      )}
      <div className="rmob-rs-row">
        <span>Pagás en el local</span>
        <strong className="beauty-rs-value">{formatMoney(holdTotals.payAtVenue)}</strong>
      </div>
      <p className="beauty-secure"><RIcon name="shield" width={14} height={14} /> Reserva segura, sin costos extra de servicio.</p>
    </>
  );

/**
 * Canchas desktop summary rows (`rs-row`): one line per service, then
 * Duración / Profesional / Fecha / Horario. `services` items are
 * `{ key, name, sub?, price }`; empty `date` / `timeRange` render a dash.
 */
export const SummaryRows = ({ services, durationLabel, professionalLabel, date, timeRange }) => (
  <>
    {services.map((service) => (
      <div className="rs-row" key={service.key}>
        <span className="k">
          <RIcon name="ticket" />
          {service.name}{service.sub && <small>{service.sub}</small>}
        </span>
        <span className="v">{formatMoney(service.price)}</span>
      </div>
    ))}
    <div className="rs-div" />
    <div className="rs-row"><span className="k"><RIcon name="clock" />Duración</span><span className="v">{durationLabel}</span></div>
    <div className="rs-row"><span className="k"><RIcon name="user" />Profesional</span><span className="v">{professionalLabel}</span></div>
    <div className="rs-row"><span className="k"><RIcon name="cal" />Fecha</span><span className="v">{date ? formatPillDate(date) : "—"}</span></div>
    <div className="rs-row"><span className="k"><RIcon name="clock" />Horario</span><span className="v">{timeRange || "—"}</span></div>
  </>
);

/** Canchas `policy-box`: booking window, cancellation and salon notes. */
export const PolicyBox = ({ salon }) => {
  const days = Number(salon.reservationWindowDays);
  const noCancel = salon.cancellationAllowed === false || salon.cancellationAllowed === "false";
  return (
    <div className="policy-box">
      {Number.isFinite(days) && days > 0 && (
        <div className="policy"><RIcon name="cal" width={16} height={16} />Las reservas se pueden realizar hasta con {days} días de antelación.</div>
      )}
      {noCancel ? (
        <div className="policy"><RIcon name="alert" width={16} height={16} style={{ color: "var(--zoco-danger)" }} />Las reservas no son cancelables.</div>
      ) : (
        <div className="policy"><RIcon name="shield" width={16} height={16} />Cancelación sin cargo hasta {salon.cancellationPolicyHours || 24} hs antes.</div>
      )}
      {salon.additionalNotes && <div className="policy"><RIcon name="info" width={16} height={16} />{salon.additionalNotes}</div>}
    </div>
  );
};

/** Canchas "Aclaración" (free) or orange seña notice shown above the consent on Tus datos. */
export const PaymentNotice = ({ hasDeposit, salon }) => {
  if (!hasDeposit) {
    return (
      <div className="beauty-notice">
        <strong>Aclaración:</strong> No pagás nada online. El precio del turno lo abonás en el local al momento de tu turno.
      </div>
    );
  }
  const noCancel = salon.cancellationAllowed === false || salon.cancellationAllowed === "false";
  return (
    <div className="beauty-notice beauty-notice--deposit">
      <span className="beauty-notice-icon"><RIcon name="info" width={18} height={18} /></span>
      <div>
        <strong>Sólo se debitará el importe de la seña</strong>
        <span>
          Abonarás el saldo restante en el local.
          {noCancel
            ? " Las reservas no son cancelables."
            : ` Si cancelás con ${salon.cancellationPolicyHours || 24} hs de anticipación, se te reintegra la seña.`}
        </span>
      </div>
    </div>
  );
};

/** Inputs of the custom fields the salon asks at booking, by type. Renders nothing without fields. */
const ClientFieldInputs = ({ flow, field, errorCls, desktop }) => {
  const { clientFields = [], fieldAnswers = {}, fieldErrors = {}, setFieldAnswer } = flow;
  return clientFields.map((f) => {
    const id = `beauty-cf-${f.id}`;
    const value = fieldAnswers[f.id];
    const error = fieldErrors[f.id];
    const star = f.required ? <span className="req"> *</span> : null;
    const wide = f.type === "longtext" || f.type === "checkbox";
    let input;
    if (f.type === "checkbox") {
      input = (
        <label htmlFor={id} className="beauty-cf-check">
          <input id={id} type="checkbox" checked={value === true} aria-invalid={Boolean(error)} onChange={(e) => setFieldAnswer(f.id, e.target.checked)} />
          <span>{f.label}{star}</span>
        </label>
      );
    } else if (f.type === "longtext") {
      input = <div className="inp beauty-inp-area"><textarea id={id} rows={3} maxLength={2000} value={value ?? ""} onChange={(e) => setFieldAnswer(f.id, e.target.value)} /></div>;
    } else if (f.type === "select") {
      input = (
        <div className="inp">
          <select id={id} value={value ?? ""} onChange={(e) => setFieldAnswer(f.id, e.target.value)}>
            <option value="">Elegí una opción</option>
            {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    } else {
      const type = f.type === "number" ? "number" : f.type === "date" ? "date" : "text";
      input = <div className="inp"><input id={id} type={type} maxLength={type === "text" ? 200 : undefined} value={value ?? ""} onChange={(e) => setFieldAnswer(f.id, e.target.value)} /></div>;
    }
    return (
      <div key={f.id} className={`${field}${desktop && wide ? " full" : ""}`}>
        {f.type !== "checkbox" && <label htmlFor={id}>{f.label}{star}</label>}
        {input}
        {error && <span className={errorCls}>{error}</span>}
      </div>
    );
  });
};

/** Contact fields. `desktop` switches to the Canchas form-card (`field`) markup. */
export const ContactFields = ({ flow, desktop = false }) => {
  const { register, errors } = flow;
  const field = desktop ? "field" : "rmob-field";
  const errorCls = desktop ? "beauty-field-error" : "rmob-field-error";
  const hintCls = desktop ? "hint" : "beauty-helper";
  return (
    <div className={desktop ? "fgrid" : undefined}>
      <div className={`${field}${desktop ? " full" : ""}`}>
        <label htmlFor="beauty-fullname">Nombre y apellido <span className="req">*</span></label>
        <div className="inp">
          <RIcon name="user" />
          <input id="beauty-fullname" placeholder="Ej. Camila Rodríguez" {...register("fullName", { required: "Ingresá tu nombre." })} />
        </div>
        {errors.fullName && <span className={errorCls}>{errors.fullName.message}</span>}
      </div>

      <div className={field}>
        <label htmlFor="beauty-email">Email <span className="req">*</span></label>
        <div className="inp">
          <RIcon name="mail" />
          <input
            id="beauty-email"
            type="email"
            placeholder="tu@email.com"
            {...register("email", { required: "Ingresá un email válido.", pattern: { value: /^\S+@\S+\.\S+$/, message: "Email inválido." } })}
          />
        </div>
        <span className={hintCls}>Te enviamos el comprobante acá.</span>
        {errors.email && <span className={errorCls}>{errors.email.message}</span>}
      </div>

      <div className={field}>
        <label>Teléfono <span className="req">*</span></label>
        <PhoneCountryInput
          countryCode={flow.countryCode}
          number={flow.watch("phone") || ""}
          onChange={flow.setPhone}
          error={errors.phone?.message}
          placeholder="11 2345 6789"
          inputProps={{ "aria-label": "Teléfono" }}
        />
        <span className={hintCls}>Por si el local necesita contactarte.</span>
        {errors.phone && <span className={errorCls}>{errors.phone.message}</span>}
      </div>

      <div className={field}>
        <label htmlFor="beauty-dni">DNI <span className="req">*</span></label>
        <div className="inp">
          <RIcon name="user" />
          <input
            id="beauty-dni"
            inputMode="numeric"
            placeholder="Tu DNI"
            onInput={(e) => { e.target.value = (e.target.value || "").replace(/\D/g, "").slice(0, 10); }}
            {...register("dni", {
              required: "Ingresá tu DNI.",
              pattern: { value: /^\d{7,10}$/, message: "DNI inválido (debe tener entre 7 y 10 números)." },
            })}
          />
        </div>
        {errors.dni && <span className={errorCls}>{errors.dni.message}</span>}
      </div>

      <div className={`${field}${desktop ? " full" : ""}`}>
        <label htmlFor="beauty-notes">Notas para el local</label>
        <div className="inp beauty-inp-area">
          <textarea
            id="beauty-notes"
            placeholder="Tipo de pelo, alergias, referencias…"
            rows={2}
            {...register("notes")}
          />
        </div>
      </div>

      <ClientFieldInputs flow={flow} field={field} errorCls={errorCls} desktop={desktop} />
    </div>
  );
};

/** "Detalles del turno" box of the data step. */
export const AppointmentDetails = ({ hold, desktop = false, ...rowProps }) => (
  desktop ? (
    <div className="reservation-details-box">
      <div className="rdb-title">Detalles del turno</div>
      <DetailRow desktop icon="ticket" label="Servicios">{resumenDeLineas(hold.services)}</DetailRow>
      <HoldDetailRows hold={hold} desktop {...rowProps} />
    </div>
  ) : (
    <div className="rmob-reserva-summary">
      <h4 className="beauty-sum-title">Detalles del turno</h4>
      <DetailRow icon="ticket" label="Servicios">{resumenDeLineas(hold.services)}</DetailRow>
      <HoldDetailRows hold={hold} {...rowProps} />
    </div>
  )
);

export const DepositNote = () => (
  <p className="beauty-info-note">
    <RIcon name="shield" width={15} height={15} />
    Te vamos a redirigir a nuestro sistema de pago seguro para confirmar la seña.
  </p>
);

/** Consent checkbox that gates the CTA. */
export const ConsentCheck = ({ flow, salon }) => {
  const policyLine = salon.cancellationAllowed === false || salon.cancellationAllowed === "false"
    ? ": las reservas no son cancelables."
    : `: cancelación sin cargo hasta ${salon.cancellationPolicyHours || 24} hs antes.`;
  return (
    <>
    <MarketingOptIn venueName={salon.name} checked={flow.marketingOptIn} onChange={flow.setMarketingOptIn} />
    <div className="consent" role="checkbox" aria-checked={flow.consent} tabIndex={0}
      onClick={() => flow.setConsent((c) => !c)}
      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); flow.setConsent((c) => !c); } }}
    >
      <span className={`cbox${flow.consent ? " on" : ""}`}>{flow.consent && <RIcon name="check" width={13} height={13} />}</span>
      <p>Acepto la <strong>política de reservas</strong> de {salon.name}{policyLine}</p>
    </div>
    </>
  );
};

/** "Elegí cómo pagar" modal: the Canchas `payment-modal` plus the deposit amount. */
export const PaymentModal = ({ flow, holdTotals }) => (
  <Modal show={flow.showPaymentModal} onHide={flow.closePaymentModal} centered backdrop="static" className="payment-modal">
    <Modal.Header closeButton><Modal.Title>Elegí cómo pagar</Modal.Title></Modal.Header>
    <Modal.Body>
      <div className="beauty-deposit-box">
        <span>Seña de reserva</span>
        <strong>{formatMoney(holdTotals.deposit)}</strong>
      </div>
      <div className="mb-3">
        <label className="form-label">Tarjeta</label>
        <QtySelect
          className="payment-select"
          placeholder="Elegí una tarjeta"
          value={flow.modalCard}
          onChange={(val) => flow.setModalCard(val == null ? "" : String(val))}
          options={(flow.cardOptions || []).map((opt) => ({ value: opt.id, label: opt.label, disabled: opt.disabled }))}
        />
      </div>
      {flow.selectedCardOption && (
        <div className="mb-3">
          <label className="form-label">Cuotas</label>
          <QtySelect
            className="payment-select"
            placeholder="Elegí la cantidad de cuotas"
            value="1"
            disabled={true}
            onChange={() => {}}
            options={[{ value: "1", label: "1 cuota (sin interés)" }]}
          />
        </div>
      )}
      <p className="beauty-info-note">Te vamos a redirigir a Fiserv para completar el pago.</p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={flow.closePaymentModal}>Cancelar</Button>
      <Button variant="primary" onClick={flow.handleConfirmPaymentSelection} disabled={!flow.selectedCardOption}>
        Continuar
      </Button>
    </Modal.Footer>
  </Modal>
);
